import { Icons } from '@shared/components/content/icons';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { cn } from '@shared/lib/utils';
import { cva } from 'class-variance-authority';

const metricTrendVariants = cva(
  'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium',
  {
    variants: {
      tone: {
        // Falling emissions are the good outcome, rising ones the bad one.
        good: 'bg-secondary-50 text-secondary-700',
        bad: 'bg-error-50 text-error-600',
        neutral: 'bg-gray-50 text-gray-600',
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  }
);

interface MetricTrendProps {
  /**
   * Percentage change against the comparison period. Renders nothing when
   * null, so callers can pass an unresolved comparison straight through.
   */
  percentChange: number | null;
  /** Names the comparison period, e.g. "vs 2024/25" — shown on hover. */
  label: string;
  /**
   * `impact` colours a fall green and a rise red, for metrics where less is
   * better. `neutral` stays muted in both directions, for metrics like spend
   * where neither direction is inherently good or bad.
   */
  variant?: 'impact' | 'neutral';
  className?: string;
}

/**
 * Change in a metric against an earlier period, as a compact tinted chip —
 * arrow and a whole percentage, with the comparison period on hover. Kept
 * small so the comparison reads as secondary to the metric itself.
 */
export function MetricTrend({
  percentChange,
  label,
  variant = 'impact',
  className,
}: MetricTrendProps) {
  if (percentChange == null) return null;

  // Direction is taken from the rounded figure so a change that displays as
  // 0% doesn't carry a contradictory arrow.
  const rounded = Math.round(percentChange);
  const direction = rounded === 0 ? 'flat' : rounded < 0 ? 'down' : 'up';

  const tone =
    variant === 'neutral' || direction === 'flat'
      ? 'neutral'
      : direction === 'down'
        ? 'good'
        : 'bad';

  const Icon =
    direction === 'flat'
      ? Icons.minus
      : direction === 'down'
        ? Icons.trendingDown
        : Icons.trendingUp;

  const directionLabel =
    direction === 'flat' ? 'No change' : direction === 'down' ? 'Down' : 'Up';

  return (
    <SimpleTooltip
      content={label}
      trigger={
        <span
          aria-label={`${directionLabel} ${Math.abs(rounded)}% ${label}`}
          className={cn(metricTrendVariants({ tone }), className)}
        >
          <Icon aria-hidden className="size-3.5 shrink-0" />
          {Math.abs(rounded)}%
        </span>
      }
    />
  );
}
